import { useMemo, useState, useCallback } from 'react';
import { fmt, DonutChart, isActiveStage } from './plShared';
import { DrillModal, DealModal, useDrill } from './DrillModal';

const TYPE_COLORS = ['#2563eb', '#0891b2', '#059669', '#d97706', '#7c3aed', '#dc2626'];
// Cycling palette for however many BUs actually exist — was hardcoded to
// exactly 2 colors (one per NA/APAC) throughout this whole page, which is
// the same anti-pattern as everything else: a real region (e.g. "India")
// silently had nowhere to render even though the backend already tracks it
// dynamically (bu_dist, weekly_bu_trend).
const BU_COLORS = ['#2563eb', '#0891b2', '#7c3aed', '#d97706', '#059669', '#dc2626', '#ea580c', '#0d9488'];
const colorForBu = index => BU_COLORS[index % BU_COLORS.length];

// BUs come straight from whatever `bu` values appear in the data rather
// than a fixed North America/APAC pair — a hardcoded list silently drops a
// real region (e.g. "India") the CRM has for reps operating there.
function dynamicBUs(deals) {
  const values = new Set();
  (deals || []).forEach(d => { const v = (d.bu || '').trim(); if (v) values.add(v); });
  return [...values].sort();
}

// Quarters come straight from close_quarter values present in the data
// rather than a fixed list — a hardcoded quarter list goes stale every
// fiscal year (and previously excluded FY28 entirely). Mirrors
// PipelineMain.js's parseQuarter/dynamicQuarterOptions convention.
function parseQuarter(value) {
  const m = /^Q(\d)\s*(\d{2})\+?$/i.exec((value || '').trim());
  return m ? { quarter: Number(m[1]), year: Number(m[2]) } : null;
}
function dynamicQuarters(deals) {
  const values = new Set();
  (deals || []).forEach(d => { const v = (d.close_quarter || '').trim(); if (v) values.add(v); });
  return [...values].sort((a, b) => {
    const pa = parseQuarter(a);
    const pb = parseQuarter(b);
    if (pa && pb) return (pa.year - pb.year) || (pa.quarter - pb.quarter);
    return a.localeCompare(b);
  });
}

// Groups deals by whatever forecast_category values actually appear,
// instead of a fixed Commit/Upside/Not Forecasted list — the CRM has
// renamed/added categories before (e.g. "Upside" became "Likely" plus a
// new "Best Case"), and a hardcoded list silently drops any deal whose
// category doesn't match one of the fixed names.
function normalizeForecast(d) {
  const fc = (d.forecast_category || '').trim();
  const lower = fc.toLowerCase();
  if (lower === 'commit') return 'Commit';
  if (lower === '' || lower === 'not forecasted') return 'Not Forecasted';
  return fc;
}
function groupByForecast(rows) {
  const byCategory = new Map();
  rows.forEach(d => {
    const category = normalizeForecast(d);
    if (!byCategory.has(category)) byCategory.set(category, []);
    byCategory.get(category).push(d);
  });
  const groups = [...byCategory.entries()].map(([category, deals]) => ({
    category,
    deals,
    amount: deals.reduce((s, d) => s + (d.amount || 0), 0),
  }));
  const rank = category => category === 'Commit' ? -1 : category === 'Not Forecasted' ? 1 : 0;
  return groups.sort((a, b) => rank(a.category) - rank(b.category) || b.amount - a.amount);
}

const STAGE_ORDER = [
  '5% - Prospecting', '20%-Discovery', '40%-Scoping',
  '60%-Propose', '80%-Validate', '90%-Negotiate & Close',
  'Business Won',
];
const ACTIVE_STAGES = STAGE_ORDER.filter(stage => stage !== 'Business Won');
const STAGE_SHORT = {
  '5% - Prospecting': '5%', '20%-Discovery': '20%', '40%-Scoping': '40%',
  '60%-Propose': '60%', '80%-Validate': '80%', '90%-Negotiate & Close': '90%',
  'Business Won': 'Won',
};

/* ── Single-BU bar chart by stage, switchable between deal Count and $ Value ── */
function BuStageSingleChart({ stageData, metric, color, onBarClick }) {
  if (!stageData.length) return null;
  const getValue = s => metric === 'value' ? s.value : s.count;
  const max = Math.max(...stageData.map(getValue), 1);
  const W = 700, H = 260, pb = 48, pt = 14, pl = metric === 'value' ? 54 : 30, pr = 14;
  const iW = W - pl - pr, iH = H - pb - pt;
  const bw = Math.min(52, iW / stageData.length * 0.5);
  const step = iW / stageData.length;
  const fmtAxis = v => metric === 'value' ? fmt(v) : Math.round(v);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="pl-trend-svg" style={{ display: 'block' }}>
      {Array.from({ length: 6 }, (_, index) => {
        const value = max * index / 5;
        const yy = pt + iH - value / max * iH;
        return <g key={index}><line x1={pl} x2={W - pr} y1={yy} y2={yy} stroke="#d7dce3" /><text x={pl - 7} y={yy + 4} textAnchor="end" fontSize="9" fill="#6b7280">{fmtAxis(value)}</text></g>;
      })}
      {stageData.map((s, i) => {
        const cx = pl + step * i + step / 2;
        const v = getValue(s);
        const h = (v / max) * iH;
        return (
          <g key={i}>
            {v > 0 && (
              <>
                <rect x={cx - bw / 2} y={pt + iH - h} width={bw} height={h} fill={`${color}29`} stroke={color} strokeWidth="2" rx={2}
                  style={{ cursor: 'pointer' }} onClick={(event) => { event.stopPropagation(); onBarClick(s.stage); }}>
                  <title>{`${STAGE_SHORT[s.stage] || s.stage}: ${metric === 'value' ? fmt(v) : v}`}</title>
                </rect>
                <text x={cx} y={pt + iH - h - 3} textAnchor="middle" fontSize="8" fill={color}>{metric === 'value' ? fmt(v) : v}</text>
              </>
            )}
            <text x={cx} y={H - 22} textAnchor="middle" fontSize="10" fill="#6b7280">
              {STAGE_SHORT[s.stage] || s.stage.slice(0, 4)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* ── Quarter × BU grouped bar chart (pipeline amount), stacked per BU ── */
function QuarterBuChart({ qData, bus, onBarClick }) {
  if (!qData.length || !bus.length) return null;
  const maxTotal = Math.max(...qData.map(q => bus.reduce((sum, bu) => sum + (q.values[bu] || 0), 0)), 1);
  const W = 700, H = 260, pb = 58, pt = 14, pl = 52, pr = 14;
  const iW = W - pl - pr, iH = H - pb - pt;
  const bw = Math.min(76, iW / qData.length * 0.62);
  const step = iW / qData.length;
  const ticks = 5;
  const y = value => pt + iH - (value / maxTotal) * iH;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="pl-trend-svg" style={{ display: 'block' }}>
      {Array.from({ length: ticks + 1 }, (_, i) => {
        const value = maxTotal * i / ticks;
        const yy = y(value);
        return <g key={i}><line x1={pl} x2={W - pr} y1={yy} y2={yy} stroke="#d7dce3" strokeWidth="1" /><text x={pl - 8} y={yy + 4} textAnchor="end" fontSize="10" fill="#6b7280">{fmt(value)}</text></g>;
      })}
      {qData.map((q, i) => {
        const cx = pl + step * i + step / 2;
        let cumulative = 0;
        return (
          <g key={i}>
            {bus.map((bu, buIndex) => {
              const value = q.values[bu] || 0;
              if (!value) return null;
              const bottom = cumulative;
              cumulative += value;
              const top = y(cumulative);
              const color = colorForBu(buIndex);
              return (
                <rect key={bu} x={cx - bw / 2} y={top} width={bw} height={y(bottom) - top} fill={`${color}29`} stroke={color} strokeWidth="2" rx="2"
                  style={{ cursor: 'pointer' }} onClick={(event) => { event.stopPropagation(); onBarClick(q.quarter, bu); }}>
                  <title>{q.quarter} — {bu}: {fmt(value)}</title>
                </rect>
              );
            })}
            <text x={cx} y={y(0) + 22} textAnchor="middle" fontSize="10" fill="#6b7280">{q.quarter}</text>
          </g>
        );
      })}
      <g transform={`translate(${W / 2 - (bus.length * 90) / 2}, ${H - 12})`}>
        {bus.map((bu, index) => (
          <g key={bu} transform={`translate(${index * 90}, 0)`}>
            <rect width="10" height="10" fill={`${colorForBu(index)}29`} stroke={colorForBu(index)} strokeWidth="2" />
            <text x="16" y="9" fontSize="10" fill="#4b5563">{bu}</text>
          </g>
        ))}
      </g>
    </svg>
  );
}

/* ── BU pipeline trend, one line per BU ── */
function BuTrendChart({ rows, bus, onBuClick }) {
  if (!rows.length || !bus.length) return null;
  const W = 700, H = 250, pad = { l: 50, r: 16, t: 14, b: 38 };
  const innerW = W - pad.l - pad.r, innerH = H - pad.t - pad.b;
  const values = rows.flatMap(row => bus.map(bu => row.bus?.[bu] || 0));
  const max = Math.max(...values, 1);
  const x = index => pad.l + (index / Math.max(rows.length - 1, 1)) * innerW;
  const y = value => pad.t + innerH - value / max * innerH;
  const makePoints = bu => rows.map((row, index) => `${x(index)},${y(row.bus?.[bu] || 0)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="pl-trend-svg" style={{ display: 'block' }}>
      {Array.from({ length: 6 }, (_, index) => {
        const value = max * index / 5;
        const yy = y(value);
        return <g key={index}><line x1={pad.l} x2={W - pad.r} y1={yy} y2={yy} stroke="#d7dce3" /><text x={pad.l - 8} y={yy + 4} textAnchor="end" fontSize="9" fill="#6b7280">{fmt(value)}</text></g>;
      })}
      {bus.map((bu, buIndex) => <polyline key={bu} points={makePoints(bu)} fill="none" stroke={colorForBu(buIndex)} strokeWidth="2.2" strokeLinejoin="round" />)}
      {bus.map((bu, buIndex) => rows.map((row, index) => <circle key={`${bu}-${index}`} cx={x(index)} cy={y(row.bus?.[bu] || 0)} r="2.5" fill={colorForBu(buIndex)} style={{ cursor: 'pointer' }} onClick={event => { event.stopPropagation(); onBuClick(bu); }}><title>{bu} W{row.week_num}: {fmt(row.bus?.[bu] || 0)}</title></circle>))}
      {rows.map((row, index) => (index % Math.max(1, Math.ceil(rows.length / 8)) === 0 || index === rows.length - 1) ? <text key={row.week} x={x(index)} y={H - 18} textAnchor="middle" fontSize="8" fill="#6b7280">W{row.week_num}</text> : null)}
    </svg>
  );
}

function Legend({ bus }) {
  return (
    <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 10, color: 'var(--sub)', flexWrap: 'wrap' }}>
      {bus.map((bu, index) => (
        <span key={bu}>
          <span style={{ display: 'inline-block', width: 10, height: 3, background: colorForBu(index), borderRadius: 1, marginRight: 4, verticalAlign: 'middle' }} />
          {bu}
        </span>
      ))}
    </div>
  );
}

function BusinessUnit({ data }) {
  const { deals = [], weekly_trend = [], weekly_bu_trend = [], selected_week_short = '' } = data;
  const { drill, activeDeal, openDrill, closeDrill, openDeal, closeDeal } = useDrill();

  const weekLabel = selected_week_short || (weekly_trend.length ? `W${weekly_trend[weekly_trend.length - 1].week_num}` : 'W—');

  const bus = useMemo(() => dynamicBUs(deals), [deals]);

  const activeDeals = useMemo(() =>
    deals.filter(d => isActiveStage(d.stage)),
    [deals]
  );
  const sumAmt = rows => rows.reduce((s, d) => s + (d.amount || 0), 0);

  const makeTypeItems = useCallback(rows => {
    const map = {};
    for (const d of rows) {
      const k = d.order_type || 'Unknown';
      map[k] = (map[k] || 0) + (d.amount || 0);
    }
    return Object.keys(map)
      .sort((a, b) => map[b] - map[a])
      .map((label, index) => ({
        label,
        value: map[label] || 0,
        color: TYPE_COLORS[index % TYPE_COLORS.length],
        onClick: () => {
          const filtered = rows.filter(d => (d.order_type || 'Unknown') === label);
          openDrill(`${label} Deals`, `${filtered.length} deals`, filtered);
        },
      }));
  }, [openDrill]);

  const makeLevelGrid = useCallback((rows, field, levelLabel, categoryLabel) => {
    const map = {};
    for (const d of rows) {
      const k = d[field] || 'Unknown';
      map[k] = (map[k] || 0) + (d.amount || 0);
    }
    const entries = Object.entries(map).sort((a, b) => b[1] - a[1]);
    if (!entries.length) return null;
    return (
      <div style={{ marginBottom: 14 }}>
        <div className="pl-card-title" style={{ marginBottom: 6 }}>{levelLabel}</div>
        <div className="rep-grid">
          {entries.map(([label, value]) => {
            const filtered = rows.filter(d => (d[field] || 'Unknown') === label);
            return (
              <div key={label} className="rc" onClick={() => openDrill(`${categoryLabel} → ${label}`, `${filtered.length} deals · ${fmt(value)}`, filtered)}>
                <div className="rc-name">{label}</div>
                <div className="rc-pipe">{fmt(value)}</div>
                <div className="rc-row"><div className="rc-stat"><div className="rc-sv">{filtered.length}</div><div className="rc-sl">Deals</div></div></div>
                <div className="pl-click-hint" style={{ marginTop: 8 }}>🔍 Click → see deals</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }, [openDrill]);
  const sourceBreakdownContent = useCallback((rows, categoryLabel) => {
    return makeLevelGrid(rows, 'source_level2', 'Deal Source Type (Level 2)', categoryLabel);
  }, [makeLevelGrid]);
  const makeSourceItems = useCallback(rows => {
    const map = {};
    for (const d of rows) {
      const k = d.source || 'Unknown';
      map[k] = (map[k] || 0) + (d.amount || 0);
    }
    return Object.keys(map)
      .sort((a, b) => map[b] - map[a])
      .map(label => ({
        label,
        value: map[label],
        onClick: () => {
          const filtered = rows.filter(d => (d.source || 'Unknown') === label);
          openDrill(`${label} Sourced Deals`, `${filtered.length} deals · ${fmt(map[label] || 0)}`, filtered, sourceBreakdownContent(filtered, label));
        },
      }));
  }, [openDrill, sourceBreakdownContent]);

  function openCategoryOverview(title, items, rows, categoryField, withSourceBreakdown = false) {
    const cards = items.map(item => {
      const filtered = rows.filter(d => (d[categoryField] || 'Unknown') === item.label);
      return <div key={item.label} className="rc" onClick={() => { closeDrill(); openDrill(`${item.label} Deals`, `${filtered.length} deals · ${fmt(item.value)}`, filtered, withSourceBreakdown ? sourceBreakdownContent(filtered, item.label) : null); }}><div className="rc-name">{item.label}</div><div className="rc-pipe">{fmt(item.value)}</div><div className="rc-row"><div className="rc-stat"><div className="rc-sv">{filtered.length}</div><div className="rc-sl">Deals</div></div></div><div className="pl-click-hint" style={{ marginTop: 8 }}>🔍 Click → see deals</div></div>;
    });
    openDrill(`${title} — Click to drill`, null, null, <div className="rep-grid">{cards}</div>);
  }

  /* ── One region block per BU that actually appears in the data, instead
     of exactly 2 fixed North America/APAC blocks. ── */
  const [stageMetricByBu, setStageMetricByBu] = useState({});
  const regions = useMemo(() => bus.map((bu, index) => {
    const active = activeDeals.filter(d => d.bu === bu);
    const won = deals.filter(d => d.bu === bu && d.stage === 'Business Won');
    const lost = deals.filter(d => d.bu === bu && d.stage === 'Business Lost');
    return {
      bu,
      index,
      color: colorForBu(index),
      active,
      won,
      lost,
      pipeline: sumAmt(active),
      wonAmt: sumAmt(won),
      lostAmt: sumAmt(lost),
      forecastGroups: groupByForecast(active),
      typeItems: makeTypeItems(active),
      srcItems: makeSourceItems(active),
      stageData: ACTIVE_STAGES.map(stage => {
        const rows = active.filter(d => d.stage === stage);
        return { stage, count: rows.length, value: sumAmt(rows) };
      }),
    };
  }), [bus, deals, activeDeals, makeTypeItems, makeSourceItems]);

  function drillBu(bu) {
    const rd = activeDeals.filter(d => d.bu === bu);
    openDrill(`${bu} — Pipeline`, `${rd.length} deals`, rd);
  }

  function openBuOverview() {
    const cards = regions.map(region => (
      <div key={region.bu} className="rc" onClick={() => { closeDrill(); drillBu(region.bu); }}>
        <div className="rc-name">{region.bu}</div><div className="rc-pipe">{fmt(region.pipeline)}</div>
        <div className="rc-row"><div className="rc-stat"><div className="rc-sv">{region.active.length}</div><div className="rc-sl">Deals</div></div></div>
        <div className="pl-click-hint" style={{ marginTop: 8 }}>🔍 Click → see BU deals</div>
      </div>
    ));
    openDrill('BU Split — Click to drill', null, null, <div className="rep-grid">{cards}</div>);
  }

  function handleStageBarClick(bu, stage) {
    const sd = activeDeals.filter(d => d.bu === bu && d.stage === stage);
    openDrill(`${bu} — ${stage} — ${sd.length} Deals`, `All ${bu} deals in ${stage} stage · Click any row for full detail`, sd);
  }

  function handleQuarterBarClick(quarter, bu) {
    const qd = activeDeals.filter(d => d.close_quarter === quarter && (!bu || d.bu === bu));
    const title = bu ? `${bu} — ${quarter} Pipeline` : `${quarter} Pipeline`;
    openDrill(title, `${qd.length} deals · Click any row for full detail`, qd);
  }

  function handleDrillStage(stage) {
    const sd = deals.filter(d => d.stage === stage);
    openDrill(stage, `${sd.length} deals in this stage`, sd);
  }

  const buItems = regions.map(region => ({
    label: region.bu, value: region.pipeline, count: region.active.length,
    color: region.color, onClick: () => drillBu(region.bu),
  }));

  const buQuarters = useMemo(() => dynamicQuarters(activeDeals), [activeDeals]);
  const quarterData = useMemo(() => buQuarters.map(quarter => ({
    quarter,
    values: Object.fromEntries(bus.map(bu => [
      bu,
      activeDeals.filter(d => d.bu === bu && d.close_quarter === quarter).reduce((sum, d) => sum + (d.amount || 0), 0),
    ])),
  })), [activeDeals, buQuarters, bus]);

  function openQuarterOverview() {
    const cards = quarterData.map(q => {
      const rows = activeDeals.filter(d => d.close_quarter === q.quarter);
      const total = bus.reduce((sum, bu) => sum + (q.values[bu] || 0), 0);
      return (
        <div key={q.quarter} className="rc" onClick={() => { closeDrill(); openDrill(`${q.quarter} Pipeline`, `${rows.length} deals · ${fmt(total)}`, rows); }}>
          <div className="rc-name">{q.quarter}</div><div className="rc-pipe">{fmt(total)}</div>
          <div className="rc-row">
            <div className="rc-stat"><div className="rc-sv">{rows.length}</div><div className="rc-sl">Deals</div></div>
            {bus.map((bu, index) => (
              <div className="rc-stat" key={bu}><div className="rc-sv" style={{ color: colorForBu(index) }}>{fmt(q.values[bu] || 0)}</div><div className="rc-sl">{bu} ({rows.filter(d => d.bu === bu).length})</div></div>
            ))}
          </div>
          <div className="pl-click-hint" style={{ marginTop: 8 }}>🔍 Click → see deals</div>
        </div>
      );
    });
    openDrill('Pipeline by Quarter — Click to drill', null, null, <div className="rep-grid">{cards}</div>);
  }

  return (
    <>
      {/* ── One KPI row per BU: total pipeline + its own forecast breakup,
           built from whatever forecast categories actually exist (see
           groupByForecast) — was 2 fixed NA/APAC rows. ── */}
      {regions.map(region => (
        <div className="krow" key={region.bu}>
          <div className="kc kb" onClick={() => drillBu(region.bu)}>
            <div className="kl">{region.bu} Total Pipeline</div>
            <div className="kv">{fmt(region.pipeline)}</div>
            <div className="kd"><span className="fl">active</span></div>
            <div className="click-hint">🔍 Click → {region.bu} deals</div>
          </div>
          {region.forecastGroups.map((group, index) => (
            <div key={group.category} className="kc" style={{ borderTop: `3px solid ${group.category === 'Commit' ? 'var(--amber)' : ['var(--purple)', '#7c3aed', '#0891b2', '#ea580c'][index % 4]}` }} onClick={() => openDrill(`${region.bu} — ${group.category}`, `${group.deals.length} deals`, group.deals)}>
              <div className="kl">{region.bu} {group.category}</div>
              <div className="kv">{fmt(group.amount)}</div>
              <div className="kd"><span className="fl">{group.deals.length} deals</span></div>
              <div className="click-hint">🔍 Click → {group.category.toLowerCase()} deals</div>
            </div>
          ))}
        </div>
      ))}

      {/* ── Won vs Shrinkage per BU ── */}
      <div className="krow" style={{ flexWrap: 'wrap' }}>
        {regions.map(region => (
          <div key={region.bu} style={{ display: 'flex', gap: 'inherit', flex: '1 1 auto' }}>
            <div className="kc kg" style={{ flex: 1 }} onClick={() => openDrill(`${region.bu} Won YTD`, `${region.won.length} deals`, region.won)}>
              <div className="kl">{region.bu} Won YTD</div>
              <div className="kv">{fmt(region.wonAmt)}</div>
              <div className="kd"><span className="up">{region.won.length} deals</span></div>
              <div className="click-hint">🔍 Click → {region.bu} won deals</div>
            </div>
            <div className="kc kr" style={{ flex: 1 }} onClick={() => openDrill(`${region.bu} Shrinkage YTD`, `${region.lost.length} deals`, region.lost)}>
              <div className="kl">{region.bu} Shrinkage YTD</div>
              <div className="kv">{fmt(region.lostAmt)}</div>
              <div className="kd"><span className="dn">{region.lost.length} deals</span></div>
              <div className="click-hint">🔍 Click → {region.bu} shrinkage deals</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── BU Split + Deal Type Mix per BU ── */}
      <div className="g3" style={{ flexWrap: 'wrap' }}>
        <div className="pl-card pl-card-clickable" onClick={openBuOverview}>
          <div className="pl-card-header">
            <div className="pl-card-title">BU Split {weekLabel} <span style={{ fontSize: 9, color: 'var(--cyan)' }}>↗ Click segment</span></div>
          </div>
          <div style={{ fontSize: 10, color: 'var(--sub)', marginBottom: 8 }}>Click segment → BU deals</div>
          <DonutChart items={buItems} radius={82} />
        </div>
        {regions.map(region => (
          <div key={region.bu} className="pl-card pl-card-clickable" onClick={() => openCategoryOverview(`${region.bu} Deal Type Mix`, region.typeItems, region.active, 'order_type')}>
            <div className="pl-card-header">
              <div className="pl-card-title">Deal Type Mix — {region.bu} {weekLabel} <span style={{ fontSize: 9, color: 'var(--cyan)' }}>↗ Click segment</span></div>
            </div>
            <div style={{ fontSize: 10, color: 'var(--sub)', marginBottom: 8 }}>Click segment → {region.bu} type deals</div>
            <DonutChart items={region.typeItems} radius={82} />
          </div>
        ))}
      </div>

      {/* ── Deal Source per BU ── */}
      <div className="g2" style={{ flexWrap: 'wrap' }}>
        {regions.map(region => (
          <div key={region.bu} className="pl-card pl-card-clickable" onClick={() => openCategoryOverview(`${region.bu} Deal Source`, region.srcItems, region.active, 'source', true)}>
            <div className="pl-card-header">
              <div className="pl-card-title">Deal Source — {region.bu} {weekLabel} <span style={{ fontSize: 9, color: 'var(--cyan)' }}>↗ Click segment</span></div>
            </div>
            <div style={{ fontSize: 10, color: 'var(--sub)', marginBottom: 8 }}>Click segment → {region.bu} source deals</div>
            <DonutChart items={region.srcItems} radius={82} />
          </div>
        ))}
      </div>

      {/* ── Trend: one line per BU ── */}
      <div className="g1">
        <div className="pl-card pl-card-clickable" onClick={openBuOverview}>
          <div className="pl-card-header">
            <div className="pl-card-title">
              BU Pipeline Trend <span style={{ fontSize: 9, color: 'var(--cyan)' }}>↗ Click</span>
            </div>
          </div>
          <div style={{ fontSize: 10, color: 'var(--sub)', marginBottom: 8 }}>BU pipeline evolution W1→{weekLabel}</div>
          {weekly_bu_trend.length > 0
            ? <BuTrendChart rows={weekly_bu_trend} bus={bus} onBuClick={drillBu} />
            : <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--sub)', fontSize: 11 }}>
                Loading trend data…
              </div>
          }
          <Legend bus={bus} />
        </div>
      </div>

      {/* ── Stage breakdown per BU — each with its own Count/Value toggle ── */}
      <div className="g2" style={{ flexWrap: 'wrap' }}>
        {regions.map(region => (
          <div key={region.bu} className="pl-card">
            <div className="pl-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <div className="pl-card-title">{region.bu} by Stage <span style={{ fontSize: 9, color: 'var(--cyan)' }}>↗ Click bar → deals</span></div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button type="button" className={`sbtn${(stageMetricByBu[region.bu] || 'count') === 'count' ? ' on' : ''}`} onClick={() => setStageMetricByBu(current => ({ ...current, [region.bu]: 'count' }))}>Count</button>
                <button type="button" className={`sbtn${stageMetricByBu[region.bu] === 'value' ? ' on' : ''}`} onClick={() => setStageMetricByBu(current => ({ ...current, [region.bu]: 'value' }))}>Value</button>
              </div>
            </div>
            <div style={{ fontSize: 10, color: 'var(--sub)', marginBottom: 8 }}>{stageMetricByBu[region.bu] === 'value' ? 'Pipeline value' : 'Deal count'} per stage — {region.bu}</div>
            <BuStageSingleChart stageData={region.stageData} metric={stageMetricByBu[region.bu] || 'count'} color={region.color} onBarClick={stage => handleStageBarClick(region.bu, stage)} />
          </div>
        ))}
      </div>

      {/* ── Quarter × BU ── */}
      <div className="g1">
        <div className="pl-card pl-card-clickable" onClick={openQuarterOverview}>
          <div className="pl-card-header">
            <div className="pl-card-title">
              Quarter × BU Pipeline <span style={{ fontSize: 9, color: 'var(--cyan)' }}>↗ Click</span>
            </div>
          </div>
          <div style={{ fontSize: 10, color: 'var(--sub)', marginBottom: 8 }}>Expected close quarter by BU</div>
          <QuarterBuChart qData={quarterData} bus={bus} onBarClick={handleQuarterBarClick} />
        </div>
      </div>

      {drill && <DrillModal {...drill} onClose={closeDrill} onDealClick={openDeal} />}
      {activeDeal && <DealModal deal={activeDeal} onClose={closeDeal} onDrillStage={handleDrillStage} />}
    </>
  );
}

export { BusinessUnit };
export default BusinessUnit;
